import React from "react";
import { Card } from "react-bootstrap";
import ReactECharts from "echarts-for-react";
import "./style.css";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

interface DonutChartProps {
  value: string;
  label: string;
}

const DonutChart: React.FC<DonutChartProps> = ({ value, label }) => {
  const numericValue = Number(value) || 0;

  // Dynamic color and status
  let color = "#A6C48A"; // Default (moderate)
  let statusLabel: string | null = null;

  if (numericValue > 75) {
    color = "#D84315"; // Red
    statusLabel = "OVER-ALLOCATED";
  } else if (numericValue <= 25) {
    color = "#7C991B"; // Green
    statusLabel = "UNDER-ALLOCATED";
  } else if(numericValue > 25 && numericValue <= 50) {
    color = "#F5B543"; // Orange
  } else if(numericValue > 50 && numericValue <= 75){
    color = "#DC894E";
  }

  return (
    <div
      style={{
        flex: 1,
        minWidth: "100px",
        maxWidth: "200px",
        aspectRatio: "1",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {/* Background ring */}
            <Pie
              data={[{ value: 100 }]}
              cx="50%"
              cy="50%"
              innerRadius="70%"
              outerRadius="90%"
              startAngle={90}
              endAngle={-270}
              dataKey="value"
            >
              <Cell fill="#f0f0f0" />
            </Pie>

            {/* Foreground ring with rounded ends */}
            <Pie
              data={[{ value: numericValue }]}
              cx="50%"
              cy="50%"
              innerRadius="70%"
              outerRadius="90%"
              startAngle={90}
              endAngle={90 - (360 * numericValue) / 100}
              dataKey="value"
              cornerRadius={20}
              isAnimationActive={false}
            >
              <Cell fill={color} />
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Center text */}
        <div
          style={{
            position: "absolute",
            top: "45%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontWeight: "bold",
            fontSize: 14,
            color: "#1f2d53",
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          {numericValue}%
          {statusLabel && (
            <div style={{ fontSize: 10, color }}>{statusLabel}</div>
          )}
        </div>
      </div>

      {/* Bottom label */}
      <div
        style={{
          marginTop: "0.5rem",
          fontSize: "14px",
          color: "#333",
          textAlign: "center",
        }}
      >
        {label}
      </div>
    </div>
  );
};


interface ResourceProps {
  resourceUtilData: any;
}

const ResourceCard: React.FC<ResourceProps> = ({ resourceUtilData }) => {

  const barData = resourceUtilData?.timeTracking?.map((data: any) => ({
    name: data.auditeeName,
    actual: data.actualTimeInDays,
    planned: data.plannedTimeInDays,
  }));

  const donutData = resourceUtilData?.auditeeWorkloadDistribution.map(
    (data: any) => ({
      label: data.auditeeName,
      value: data.workloadPercentage.toString(),
    })
  );

  const getBarPosition = () => {
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          let content = `<strong>${params[0].name}</strong><br/>`;
          params.forEach((item: any) => {
            content += `${item.marker} ${item.seriesName}: ${item.value}<br/>`;
          });
          return content;
        },
      },
      legend: {
        top: 10,
        right: 10,
        data: ["Actual Day(s)", "Planned Day(s)"],
      },
      grid: {
        top: 60,
        left: 40,
        right: 20,
        bottom: 40,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: barData?.map((d: any) => d.name),
        axisTick: { alignWithLabel: true },
        axisLine: { lineStyle: { color: "#000" } },
        splitLine: { show: false },
      },
      dataZoom: [
        {
          type: "slider",
          show: true,
          xAxisIndex: 0,
          height: 20,
          bottom: 10,
          start: 0,
          end: 50,
        },
        {
          type: "inside",
          xAxisIndex: 0,
          start: 0,
          end: 50,
        },
      ],
      yAxis: {
        type: "value",
        name: "Days",
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 40,
        splitLine: { show: true, lineStyle: { type: "dashed", color: "#ccc" } },
        axisLine: { show: false },
      },
      series: [
        {
          name: "Actual Day(s)",
          type: "bar",
          data: barData?.map((d: any) => d.actual),
          barGap: 0,
          barWidth: 20,
          itemStyle: { color: "#c6d48e" },
        },
        {
          name: "Planned Day(s)",
          type: "bar",
          data: barData?.map((d: any) => d.planned),
          barWidth: 20,
          itemStyle: { color: "#efb3aa" },
        },
      ],
    };
  };

  return (
    <Card.Body>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <h6 className="fw-bold mb-0" style={{ fontSize: "15px", color: "#102250" }}>
          Resource Utilization
        </h6>
      </div>

      <div>
        <div className="my-3 overalltitle my-2" style={{ position: "absolute" }}>
          Time Tracking:
        </div>
        <div>
          <ReactECharts option={getBarPosition()} />
        </div>
      </div>

      <div>
        <div className="my-3 overalltitle my-2">Workload Distribution:</div>
        <div
          className="custom_scroll"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, auto)",
            justifyContent: "space-evenly",
            gap: "2rem",
            overflowY: "auto",
            maxHeight: "400px",
            paddingRight: "0.5rem",
          }}
        >
          {donutData?.map((item : any, index : any) => (
            <DonutChart key={index} value={item.value} label={item.label} />
          ))}
        </div>
        {donutData.length === 0 && (
          <div className="emptyMessage">No Data Available</div>
        )}
      </div>
    </Card.Body>
  );
};

export default ResourceCard;
